import { Injectable } from '@angular/core';
import type { SavedFilterFindInterface } from '@zaoblako/shared/modules/personalization/dtos/saved-filter/saved-filter-find.dto';
import { ReportTypeEnum } from '@zaoblako/shared/modules/personalization/enums/report.type.enum';
import type { RequiredAnalyticalFeaturesInterface } from '@zaoblako/shared/modules/catalog/dtos/required-analytical-features.dto';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import { ApplicationAccessControlUtilsClass } from '@zaoblako/shared/modules/security/classes/application-access-control-utils.class';
import { AccessLevelEnum } from '@zaoblako/shared/modules/security/enums/access.level.enum';
import { SessionModeEnum } from '@zaoblako/shared/modules/security/enums/session-mode.enum';
import { BasisAccessRulesKeyEnum } from '@zaoblako/web/core/models/enums/access-rules/basis/basis-access-rules-key.enum';
import { BasisAccessRulesConst } from '@zaoblako/web/core/models/enums/access-rules/basis/basis-access-rules.const';
import { FinanceAccessRulesKeyEnum } from '@zaoblako/web/core/models/enums/access-rules/finance/finance-access-rules-key.enum';
import { FinanceAccessRulesConst } from '@zaoblako/web/core/models/enums/access-rules/finance/finance-access-rules.const';
import type { GlobalInfoInterface } from '@zaoblako/web/core/models/global.info.interface';
import { BasisSystemAppMetadataService } from '@zaoblako/web/core/services/basis/basis-system-app-metadata.service';
import { PersonalizationService } from '@zaoblako/web/core/services/basis/personalization.service';
import { PlanFactSettingsService } from '@zaoblako/web/core/services/budget/plan-fact-settings.service';
import { CurrentTimeZoneService } from '@zaoblako/web/core/services/current-time-zone.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { SettingsService } from '@zaoblako/web/core/services/settings.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { isNil } from 'lodash';
import { BehaviorSubject, forkJoin, Observable, of, shareReplay, Subject, switchMap, tap } from 'rxjs';

const INITIAL_GLOBAL_STATE: GlobalInfoInterface = {
  isLoaded: false,
  requiredAnalyticalFeatures: null,
  tenantInfo: null,
  accessControlRules: null,
  savedFilters: [],
  config: null,
  planFactSettings: null
};

@Injectable({
  providedIn: 'root'
})
export class GlobalService extends StoreService<GlobalInfoInterface> {
  protected state = new BehaviorSubject<GlobalInfoInterface>({ ...INITIAL_GLOBAL_STATE });

  private loadData$: Observable<unknown> | null = null;

  public showWelcome$: Subject<boolean> = new Subject<boolean>();

  public get globalInfo(): GlobalInfoInterface {
    return this.state.value;
  }

  public constructor(
    private readonly authService: AuthService,
    private readonly tenantService: TenantService,
    private readonly settingsService: SettingsService,
    private readonly planFactSettingsService: PlanFactSettingsService,
    private readonly userService: UserService,
    private readonly personalizationService: PersonalizationService,
    private readonly currentTimeZoneService: CurrentTimeZoneService,
    private readonly basisSystemAppMetadataService: BasisSystemAppMetadataService
  ) {
    super();
  }

  public loadData(): Observable<unknown> {
    return this.ensureGlobalDataLoaded();
  }

  public ensureGlobalDataLoaded(): Observable<unknown> {
    if (this.shouldReloadTenantGlobalData()) {
      this.loadData$ = null;
    }

    if (!this.loadData$) {
      this.loadData$ = this.createLoadDataPipeline().pipe(shareReplay(1));
    }

    return this.loadData$;
  }

  public resetSessionState(): void {
    this.loadData$ = null;
    this.updateState({ ...INITIAL_GLOBAL_STATE });
  }

  public needsGlobalDataLoad(): boolean {
    if (!this.globalInfo.isLoaded) {
      return true;
    }

    return this.isTenantModuleAccessMissing();
  }

  public isReadyForPermissionCheck(requiresModuleAccess: boolean): boolean {
    if (!this.globalInfo.isLoaded) {
      return false;
    }

    if (!requiresModuleAccess) {
      return true;
    }

    if (this.authService.currentProfile?.sessionMode !== SessionModeEnum.TENANT) {
      return true;
    }

    return !isNil(this.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl);
  }

  private isTenantModuleAccessMissing(): boolean {
    if (this.authService.currentProfile?.sessionMode !== SessionModeEnum.TENANT) {
      return false;
    }

    return isNil(this.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl);
  }

  private shouldReloadTenantGlobalData(): boolean {
    return this.isTenantModuleAccessMissing();
  }

  private createLoadDataPipeline(): Observable<unknown> {
    if (this.authService.currentProfile?.sessionMode === SessionModeEnum.TENANT) {
      return forkJoin({
        tenantInfo: this.tenantService.getCurrent(),
        settings: this.settingsService.getRequiredAnalyticalFeatures(),
        accessControlRules: this.userService.getAccessControlRules(),
        config: this.basisSystemAppMetadataService.getConfig()
      }).pipe(
        switchMap((data) => {
          this.updateState({ accessControlRules: data.accessControlRules });

          const canReadFilters = this.isAccessRule(
            BasisAccessRulesConst[BasisAccessRulesKeyEnum.BASIS_SAVED_FILTER_READ]
          );
          const filters$ = canReadFilters ? this.personalizationService.getSavedFilter() : of([]);

          return forkJoin([of(data), filters$]);
        }),
        tap({
          next: ([data, savedFilters]) => {
            this.updateState({
              isLoaded: true,
              requiredAnalyticalFeatures: data.settings,
              tenantInfo: data.tenantInfo,
              accessControlRules: data.accessControlRules,
              savedFilters,
              config: data.config
            });

            this.currentTimeZoneService.setTimeZone(data.tenantInfo.timezone);

            const canReadSettings = this.isAccessRule(
              FinanceAccessRulesConst[FinanceAccessRulesKeyEnum.FINANCE_PLAN_FACT_SETTINGS_READ]
            );
            if (canReadSettings) {
              this.planFactSettingsService
                .getSettings()
                .subscribe((planFactSettings) => this.updateState({ planFactSettings }));
            }
          }
        })
      );
    }

    return of(null).pipe(
      tap({
        next: () => {
          this.updateState({
            isLoaded: true
          });
        }
      })
    );
  }

  public updateRequiredAnalyticalFeatures(requiredAnalyticalFeatures: RequiredAnalyticalFeaturesInterface) {
    this.updateState({
      requiredAnalyticalFeatures: { ...requiredAnalyticalFeatures }
    });
  }

  public updateTenant(tenantInfo: TenantFindInterface) {
    this.updateState({
      tenantInfo: { ...tenantInfo }
    });
  }

  public reloadAccessControlRules() {
    this.userService.getAccessControlRules().subscribe((accessControlRules) => {
      this.updateState({
        accessControlRules: { ...accessControlRules }
      });
    });
  }

  public isAccessRule(expression: string | Record<string, any>): boolean {
    const accessControl = this.globalInfo.accessControlRules;

    if (!accessControl) {
      return false;
    }

    if (typeof expression === 'string') {
      const parts = expression.split(':');
      if (parts.length !== 3) {
        return false;
      }
      const [moduleKey, sectionKey, ruleKey] = parts;
      const section = accessControl[moduleKey]?.sections?.[sectionKey];

      if (!section) {
        return false;
      }

      if (section.permissions[ruleKey]) {
        return section.permissions[ruleKey].isAllowed;
      } else if (section.tabs[ruleKey]) {
        return section.tabs[ruleKey].accessLevel > AccessLevelEnum.FORBIDDEN;
      } else {
        return false;
      }
    } else if (typeof expression === 'object' && expression !== null) {
      return ApplicationAccessControlUtilsClass.getInstance().test(accessControl, expression);
    }

    return false;
  }

  public getFiltersByType(type: ReportTypeEnum): SavedFilterFindInterface[] {
    return this.globalInfo.savedFilters?.filter((filter) => filter.report === type);
  }

  public isEnableModule(moduleName: string): boolean {
    return this.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.[moduleName]?.enabled;
  }

  public updateFilter(savedFilter: SavedFilterFindInterface) {
    const savedFilters = this.globalInfo.savedFilters?.filter((filter) => filter.id !== savedFilter.id);
    savedFilters.push(savedFilter);
    this.updateState({
      savedFilters: [...savedFilters]
    });
  }

  public addFilter(savedFilter: SavedFilterFindInterface) {
    const savedFilters = this.globalInfo.savedFilters;
    savedFilters.push(savedFilter);
    this.updateState({
      savedFilters: [...savedFilters]
    });
  }

  public deleteFilter(savedFilter: SavedFilterFindInterface) {
    const savedFilters = this.globalInfo.savedFilters?.filter((filter) => filter.id !== savedFilter.id);
    this.updateState({
      savedFilters: [...savedFilters]
    });
  }

  public setTimerShowWelcomeSystem(time: number) {
    setTimeout(() => {
      const tenantInfo = this.globalInfo.tenantInfo;
      tenantInfo.showWelcome = true;
      this.updateState({
        tenantInfo: { ...tenantInfo }
      });
      this.showWelcome$.next(this.globalInfo.tenantInfo.showWelcome);
    }, time);
  }
}
